import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { apiFetch } from "@prescreen/lib/api-client"
import type { Integration, IntegrationOAuthSecret } from "@prescreen/lib/types"
import TabNavigation from "@/components/TabNavigation"
import UserInfo from "@/components/UserInfo"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { OAuthCredentialsDialog } from "@/components/OAuthCredentialsDialog"
import { RotateWebhookSecretDialog } from "@/components/RotateWebhookSecretDialog"
import { AddIntegrationSecretDialog } from "@/components/AddIntegrationSecretDialog"
import { EditIntegrationSecretDialog } from "@/components/EditIntegrationSecretDialog"
import { DeactivateIntegrationSecretDialog } from "@/components/DeactivateIntegrationSecretDialog"
import { OAuthSecretUsageDialog } from "@/components/OAuthSecretUsageDialog"
import { ArrowLeft, ChevronLeft, ChevronRight, ClipboardCopy, Eye, MoreVertical, Pencil, PowerOff } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  integrationHasConfigurableOAuthSecrets,
  oauthTokenScopeParameterFromSecret,
} from "@/lib/oauth-secret-scope-builder"

const PAGE_SIZE = 5

type SecretRowActionsProps = {
  secret: IntegrationOAuthSecret
  active: boolean
  editDisabled: boolean
  editDisabledTitle?: string
  scopeCopiedTooltipOpen: boolean
  onScopeCopiedTooltipOpenChange: (open: boolean) => void
  onViewUsage: (secret: IntegrationOAuthSecret) => void
  onCopyScopeValues: (secret: IntegrationOAuthSecret) => void
  onEdit: (secret: IntegrationOAuthSecret) => void
  onDeactivate: (secret: IntegrationOAuthSecret) => void
}

function SecretRowActions({
  secret,
  active,
  editDisabled,
  editDisabledTitle,
  scopeCopiedTooltipOpen,
  onScopeCopiedTooltipOpenChange,
  onViewUsage,
  onCopyScopeValues,
  onEdit,
  onDeactivate,
}: SecretRowActionsProps) {
  const scopeString = oauthTokenScopeParameterFromSecret(secret)
  const canCopyScopes = scopeString.length > 0

  return (
    <DropdownMenu>
      <Tooltip open={scopeCopiedTooltipOpen} onOpenChange={onScopeCopiedTooltipOpenChange}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              className="shrink-0"
              aria-label="Secret actions"
            >
              <MoreVertical className="size-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="left">Scope copied!</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuItem className="gap-2" onSelect={() => onViewUsage(secret)}>
          <Eye className="size-4" aria-hidden />
          View usage
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2"
          disabled={!canCopyScopes}
          title={
            !canCopyScopes
              ? "This secret has no permitted scopes configured."
              : "Copy space-separated scopes for the OAuth token request"
          }
          onSelect={() => onCopyScopeValues(secret)}
        >
          <ClipboardCopy className="size-4" aria-hidden />
          Copy Scope Values
        </DropdownMenuItem>
        {active ? (
          <>
            <DropdownMenuItem
              disabled={editDisabled}
              title={editDisabled && editDisabledTitle ? editDisabledTitle : undefined}
              onSelect={() => onEdit(secret)}
              className="gap-2"
            >
              <Pencil className="size-4" aria-hidden />
              Edit Secret
            </DropdownMenuItem>
            <DropdownMenuItem
              className="gap-2 text-destructive focus:bg-destructive/10 focus:text-destructive"
              onSelect={() => onDeactivate(secret)}
            >
              <PowerOff className="size-4" aria-hidden />
              Deactivate
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function sortSecretsForDisplay(secrets: IntegrationOAuthSecret[]): IntegrationOAuthSecret[] {
  return [...secrets].sort((a, b) => {
    const aActive = a.isActive !== false
    const bActive = b.isActive !== false
    if (aActive === bActive) {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    }
    return aActive ? -1 : 1
  })
}

export default function IntegrationSecretsPage() {
  const { integrationId } = useParams<{ integrationId: string }>()
  const [integration, setIntegration] = useState<Integration | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [showAddSecret, setShowAddSecret] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [deactivateTarget, setDeactivateTarget] = useState<IntegrationOAuthSecret | null>(null)
  const [deactivating, setDeactivating] = useState(false)
  const [editTarget, setEditTarget] = useState<IntegrationOAuthSecret | null>(null)
  const [editingSecret, setEditingSecret] = useState(false)
  const [usageSecret, setUsageSecret] = useState<IntegrationOAuthSecret | null>(null)
  const [scopeCopiedForSecretId, setScopeCopiedForSecretId] = useState<string | null>(null)
  const [stagedOAuth, setStagedOAuth] = useState<{
    clientId: string
    clientSecret: string
    secretId?: string
  } | null>(null)
  const [rotateWebhookOpen, setRotateWebhookOpen] = useState(false)
  const [rotatingWebhook, setRotatingWebhook] = useState(false)
  const [stagedWebhook, setStagedWebhook] = useState<{
    clientId: string
    webhookSecret: string
  } | null>(null)

  const loadIntegration = useCallback(async () => {
    if (!integrationId) {
      return
    }
    setLoading(true)
    setError(null)
    try {
      const response = await apiFetch(`/api/integrations/${integrationId}`)
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success || !result.data?.integration) {
        throw new Error(typeof result.error === "string" ? result.error : "Integration not found")
      }
      setIntegration(result.data.integration as Integration)
      setPage(1)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load integration")
      setIntegration(null)
    } finally {
      setLoading(false)
    }
  }, [integrationId])

  useEffect(() => {
    void loadIntegration()
  }, [loadIntegration])

  const secretsSorted = useMemo(
    () => sortSecretsForDisplay(integration?.oauthSecrets ?? []),
    [integration?.oauthSecrets],
  )

  const activeCount = useMemo(
    () => secretsSorted.filter((s) => s.isActive !== false).length,
    [secretsSorted],
  )

  const totalPages = Math.max(1, Math.ceil(secretsSorted.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageSlice = secretsSorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [page, totalPages])

  const handleAddSecretSubmit = async (payload: {
    name: string
    description?: string
    grantedScopes: string[]
  }) => {
    if (!integration?._id) {
      return
    }
    setActionError(null)
    setScopeCopiedForSecretId(null)
    setGenerating(true)
    try {
      const response = await apiFetch(`/api/integrations/${integration._id}/oauth-secrets`, {
        method: "POST",
        body: JSON.stringify(payload),
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(
          typeof result.error === "string" ? result.error : "Failed to create secret",
        )
      }
      const incoming = result.data?.integration as Integration | undefined
      const plainSecret = result.data?.clientSecret as string | undefined
      const returnedClientId = (
        (result.data?.clientId as string | undefined) ?? incoming?.clientId
      )
      const newSecretId = result.data?.newSecretId as string | undefined
      if (!incoming || !returnedClientId || !plainSecret) {
        throw new Error("Server did not return the new secret.")
      }
      setIntegration(incoming)
      setStagedOAuth({
        clientId: returnedClientId,
        clientSecret: plainSecret,
        secretId: newSecretId,
      })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to create secret")
    } finally {
      setGenerating(false)
    }
  }

  const handleRotateWebhookSecret = async () => {
    if (!integration?._id) {
      return
    }
    setActionError(null)
    setRotatingWebhook(true)
    try {
      const response = await apiFetch(
        `/api/integrations/${integration._id}/webhook-secret/rotate`,
        { method: "POST" },
      )
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(
          typeof result.error === "string" ? result.error : "Failed to rotate webhook secret",
        )
      }
      const incoming = result.data?.integration as Integration | undefined
      const webhookSecret =
        (typeof result.data?.webhookSecret === "string" ? result.data.webhookSecret : undefined) ??
        (typeof incoming?.webhookSecret === "string" ? incoming.webhookSecret : undefined)
      const clientId = incoming?.clientId ?? integration.clientId
      if (!incoming || !webhookSecret) {
        throw new Error("Server did not return the new webhook secret.")
      }
      delete incoming.webhookSecret
      setIntegration(incoming)
      setRotateWebhookOpen(false)
      setStagedWebhook({ clientId, webhookSecret })
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to rotate webhook secret")
    } finally {
      setRotatingWebhook(false)
    }
  }

  const handleEditSecretSubmit = async (payload: {
    name: string
    description?: string
    grantedScopes: string[]
  }) => {
    if (!integration?._id || !editTarget) {
      return
    }
    const secretId = editTarget.secretId
    setActionError(null)
    setScopeCopiedForSecretId(null)
    setEditingSecret(true)
    try {
      const response = await apiFetch(`/api/integrations/${integration._id}/oauth-secrets/${secretId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(typeof result.error === "string" ? result.error : "Failed to update secret")
      }
      const updated = result.data?.integration as Integration | undefined
      if (updated) {
        setIntegration(updated)
      }
      setEditTarget(null)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to update secret")
    } finally {
      setEditingSecret(false)
    }
  }

  const handleDeactivateConfirm = async () => {
    if (!integration?._id || !deactivateTarget) {
      return
    }
    const secretId = deactivateTarget.secretId
    setActionError(null)
    setScopeCopiedForSecretId(null)
    setDeactivating(true)
    try {
      const response = await apiFetch(
        `/api/integrations/${integration._id}/oauth-secrets/${secretId}/deactivate`,
        { method: "POST", body: JSON.stringify({}) },
      )
      const result = await response.json().catch(() => ({}))
      if (!response.ok || !result.success) {
        throw new Error(
          typeof result.error === "string" ? result.error : "Failed to deactivate secret",
        )
      }
      const updated = result.data?.integration as Integration | undefined
      if (updated) {
        setIntegration(updated)
      }
      setDeactivateTarget(null)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to deactivate secret")
    } finally {
      setDeactivating(false)
    }
  }

  const handleCopyScopeValues = useCallback(async (secret: IntegrationOAuthSecret) => {
    const text = oauthTokenScopeParameterFromSecret(secret)
    if (!text) {
      setScopeCopiedForSecretId(null)
      setActionError("This secret has no permitted scopes to copy.")
      return
    }
    setActionError(null)
    try {
      await navigator.clipboard.writeText(text)
      setScopeCopiedForSecretId(secret.secretId)
      window.setTimeout(() => setScopeCopiedForSecretId(null), 2500)
    } catch {
      setScopeCopiedForSecretId(null)
      setActionError("Could not copy to clipboard. Check browser permissions.")
    }
  }, [])

  if (!integrationId) {
    return null
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-4xl px-4">
          <p className="text-center text-gray-500">Loading…</p>
        </div>
      </div>
    )
  }

  if (error || !integration) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-4xl px-4 space-y-4">
          <p className="text-red-600">{error ?? "Not found"}</p>
          <Button type="button" variant="outline" asChild>
            <Link to="/integrations">Back to integrations</Link>
          </Button>
        </div>
      </div>
    )
  }

  const canConfigureBankingSecrets = integrationHasConfigurableOAuthSecrets(integration)

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-4xl px-4">
        <div className="mb-8 flex justify-between items-center">
          <div className="flex-1 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Financial Integration API</h1>
          </div>
          <div className="flex-shrink-0">
            <UserInfo />
          </div>
        </div>

        <div className="space-y-4">
          <TabNavigation />
        </div>

        <OAuthCredentialsDialog
          open={stagedOAuth !== null}
          onOpenChange={(open) => {
            if (!open) {
              setStagedOAuth(null)
            }
          }}
          title="New OAuth secret"
          description="Save this secret securely; it will not be shown again."
          clientId={stagedOAuth?.clientId ?? ""}
          clientSecret={stagedOAuth?.clientSecret ?? ""}
          secretId={stagedOAuth?.secretId}
        />

        <OAuthCredentialsDialog
          open={stagedWebhook !== null}
          onOpenChange={(open) => {
            if (!open) {
              setStagedWebhook(null)
            }
          }}
          title="New webhook secret"
          description="This replaces the previous webhook secret. Save it now; it will not be shown again."
          clientId={stagedWebhook?.clientId ?? ""}
          webhookSecret={stagedWebhook?.webhookSecret}
        />

        <RotateWebhookSecretDialog
          open={rotateWebhookOpen}
          onOpenChange={setRotateWebhookOpen}
          onConfirm={handleRotateWebhookSecret}
          isProcessing={rotatingWebhook}
        />

        {integration ? (
          <AddIntegrationSecretDialog
            open={showAddSecret}
            onOpenChange={(open) => {
              setShowAddSecret(open)
            }}
            integration={integration}
            onSubmit={handleAddSecretSubmit}
            isSubmitting={generating}
          />
        ) : null}

        <EditIntegrationSecretDialog
          open={editTarget !== null}
          onOpenChange={(open) => {
            if (!open && !editingSecret) {
              setEditTarget(null)
            }
          }}
          integration={integration}
          secret={editTarget}
          onSubmit={handleEditSecretSubmit}
          isSubmitting={editingSecret}
        />

        <OAuthSecretUsageDialog
          open={usageSecret !== null}
          onOpenChange={(open) => {
            if (!open) {
              setUsageSecret(null)
            }
          }}
          secret={usageSecret}
        />

        <DeactivateIntegrationSecretDialog
          open={deactivateTarget !== null}
          onOpenChange={(open) => {
            if (!open && !deactivating) {
              setDeactivateTarget(null)
            }
          }}
          secretName={deactivateTarget?.name ?? ""}
          maskedTail={
            deactivateTarget?.secretTail ? `••••${deactivateTarget.secretTail}` : ""
          }
          onConfirm={handleDeactivateConfirm}
          isProcessing={deactivating}
        />

        <div className="mt-6 space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="ghost" size="sm" className="gap-2 px-0" asChild>
              <Link to="/integrations">
                <ArrowLeft className="size-4" aria-hidden />
                Integrations
              </Link>
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{integration.name}</CardTitle>
              <CardDescription>
                Client ID{" "}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{integration.clientId}</code>
                {" · "}
                <span className="text-foreground">{activeCount} active secret(s)</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {actionError ? (
                <div
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                  role="alert"
                >
                  {actionError}
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">Webhook secret</p>
                  <p className="text-sm text-muted-foreground">
                    Signs outbound webhooks. Rotate to issue a new value. The current secret is not shown.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRotateWebhookOpen(true)}
                  disabled={rotatingWebhook}
                >
                  Rotate webhook secret
                </Button>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-gray-900">Secrets</h2>
                <Button
                  type="button"
                  onClick={() => setShowAddSecret(true)}
                  disabled={generating || !canConfigureBankingSecrets}
                  title={
                    !canConfigureBankingSecrets
                      ? "Configure Equifax or Jack Henry products on this integration before adding secrets."
                      : undefined
                  }
                >
                  Add secret
                </Button>
              </div>

              {secretsSorted.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No secrets yet. Add one to obtain a client secret and configure token permissions.
                </p>
              ) : (
                <>
                  <TooltipProvider delayDuration={200}>
                  <ul className="divide-y rounded-lg border">
                    {pageSlice.map((secret) => {
                      const active = secret.isActive !== false
                      return (
                        <li
                          key={secret.secretId}
                          className="flex flex-row gap-2 px-4 py-3 items-center justify-between"
                        >
                          <div className="min-w-0 space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">{secret.name}</span>
                              <Badge variant={active ? "default" : "secondary"}>
                                {active ? "Active" : "Deactivated"}
                              </Badge>
                            </div>
                            {secret.description ? (
                              <p className="text-sm text-muted-foreground">{secret.description}</p>
                            ) : null}
                            <p className="text-xs text-muted-foreground">
                              Ends in <code className="text-xs">••••{secret.secretTail}</code>
                              {" · "}
                              Created {new Date(secret.createdAt).toLocaleString()}
                            </p>
                          </div>
                          <SecretRowActions
                            secret={secret}
                            active={active}
                            editDisabled={
                              !canConfigureBankingSecrets || editingSecret || generating
                            }
                            editDisabledTitle={
                              !canConfigureBankingSecrets
                                ? "Configure Equifax or Jack Henry products on this integration before editing secrets."
                                : undefined
                            }
                            scopeCopiedTooltipOpen={scopeCopiedForSecretId === secret.secretId}
                            onScopeCopiedTooltipOpenChange={(open) => {
                              if (!open) {
                                setScopeCopiedForSecretId((id) =>
                                  id === secret.secretId ? null : id,
                                )
                              }
                            }}
                            onViewUsage={(s) => setUsageSecret(s)}
                            onCopyScopeValues={handleCopyScopeValues}
                            onEdit={(s) => setEditTarget(s)}
                            onDeactivate={(s) => setDeactivateTarget(s)}
                          />
                        </li>
                      )
                    })}
                  </ul>
                  </TooltipProvider>

                  {totalPages > 1 ? (
                    <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                      <p className="text-sm text-muted-foreground">
                        Page {safePage} of {totalPages} ({secretsSorted.length} total)
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={safePage <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          aria-label="Previous page"
                        >
                          <ChevronLeft className="size-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={safePage >= totalPages}
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          aria-label="Next page"
                        >
                          <ChevronRight className="size-4" />
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
